<template>
  <modal v-bind="$attrs" v-on="$listeners" :center="true" :show-close="false" width="848px">
    <template v-slot:title>
      <h1>{{ $t("homework.modals.assign_to_class.assign_to_class") }}</h1>
    </template>
    <template v-slot:body>
      <div v-loading="primaryLoading">
        <div class="section">
          <h6 class="section-header">{{ $t("homework.modals.assign_to_class.selected_assignment") }}</h6>
          <el-form class="assignment-name-from">
            <el-row :gutter="24" class="form-row">
              <el-col :md="24" :xs="24">
                <elsa-input v-model="name" :disabled="true" name="name" class="assignment-name" />
              </el-col>
            </el-row>
          </el-form>
        </div>
        <div class="section">
          <h6 class="section-header" style="margin-bottom: 24px;margin-top: 8px;">
            {{ $t("homework.modals.assign_to_class.select_a_class_to_assign_to") }}
          </h6>
          <div class="section-container">
            <el-row type="flex" style="width: 100%" justify="center">
              <el-col :span="24">
                <ul class="assigned-classes-list" v-for="(item, index) in assignments" :key="index">
                  <li>
                    <assign-to-class-detail :index="index" :assignment-name="name" ref="AssignToClassDetail" />
                  </li>
                </ul>
              </el-col>
            </el-row>
          </div>
          <el-button
            type="text"
            class="invite-teacher"
            @click="handleAssignToAnotherClass"
            style="margin-bottom: 28px; margin-top: -16px"
          >
            <font-awesome-icon :icon="['fas', 'plus']" class="pr-px-8" />{{
              $t($i18nLabelsHelper(currentOrganizationType, `study_sets.assign_to_another_group`))
            }}
          </el-button>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <tertiary-button class="confirm-cancel" @click.native="handleLeave">
        {{ $t("common.cancel") }}
      </tertiary-button>
      <primary-button
        class="confirm-delete"
        v-loading="primaryLoading"
        ref=""
        @click.native="handleCreate"
        :disabled="!primaryButtonEnabled"
      >
        {{ $t("common.assign") }}
      </primary-button>
    </template>
  </modal>
</template>

<script>
import ElsaInput from "@/components/common/Input";
import Modal from "@/components/common/Modal";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import AssignToClassDetail from "./assign-to-class/AssignToClassDetail";
import { mapActions, mapGetters } from "vuex";
import { successMessage, warningMessage } from "@/utils/messages";
import { always, isNil, path, dissoc, pathOr, length, compose, propOr, objOf, map } from "ramda";
import { DefaultShareableOptions, DefaultCategoryOptions } from "@/constants/studySets";
import { getOptionsLabels } from "@/utils/elementUi";

export default {
  components: {
    Modal,
    ElsaInput,
    PrimaryButton,
    TertiaryButton,
    AssignToClassDetail,
  },
  props: {
    selectedAssignment: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      primaryLoading: false,
      name: "",
      emptyCheckStatus: 0,
    };
  },
  computed: {
    ...mapGetters({
      buildingAssignments: "homeworks/getBuildingAssignment",
      currentOrganizationType: "organization/currentOrganizationType",
      getAssignmentLibraryDetail: "homeworks/getAssignmentLibraryDetail",
    }),
    assignments() {
      return pathOr([], ["buildingAssignments", "assignments"])(this);
    },
    assignedStudySets() {
      return pathOr([], ["buildingAssignments", "study_sets"])(this);
    },
    lessonOriginalIds() {
      return pathOr([], ["buildingAssignments", "lesson_original_ids"])(this);
    },
    assignmentLibraryDetail() {
      return this.getAssignmentLibraryDetail;
    },
    primaryButtonEnabled() {
      return (
        this.assignments.length > 0 &&
        this.assignments.every(x => {
          return x.name && x.groupIds && x.groupIds.length && x.startDate != null && x.startTime && x.dueDate && x.dueTime;
        })
      );
    },
  },
  methods: {
    ...mapActions({
      getAssignmentLibrary: "homeworks/getAssignmentLibrary",
      createAssignments: "homeworks/createAssignments",
      updateBuildingAssignment: "homeworks/updateBuildingAssignment",
      resetAssignments: "homeworks/resetHomeworkAssignment",
      createAssignmentLibrary: "homeworks/createAssignmentLibrary",
      resetAssignmentLibrary: "homeworks/resetAssignmentLibrary",
    }),
    getCategoryLabel(v) {
      return getOptionsLabels(DefaultCategoryOptions, v);
    },
    handleLeave() {
      this.$emit("closed");
    },
    async handleCreate() {
      await this.getAssignmentLibrary();
      const assignmentLibrary = this.assignmentLibraryDetail;
      const assignedStudySets = this.assignedStudySets;
      const lessonOriginalIds = this.lessonOriginalIds;
      const readAloudIds = [];
      const aiTutorIds = [];
      for (let i = 0; i < assignmentLibrary.length; i++) {
        if (assignmentLibrary[i].study_set != null) {
          assignedStudySets.push(assignmentLibrary[i].study_set);
        }
        if (assignmentLibrary[i].lesson_original_id != null) {
          lessonOriginalIds.push(assignmentLibrary[i].lesson_original_id);
        }
        if (assignmentLibrary[i].read_aloud != null) {
          readAloudIds.push(assignmentLibrary[i].read_aloud);
        }
        if (assignmentLibrary[i].ai_tutor != null) {
          aiTutorIds.push(assignmentLibrary[i].ai_tutor);
        }
      }
      this.updateBuildingAssignment({
        study_sets: assignedStudySets,
        lesson_original_ids: lessonOriginalIds,
        assignments: this.assignments,
        read_aloud: readAloudIds,
        ai_tutor: aiTutorIds
      });
      try {
        this.primaryLoading = this.$loading({
          lock: true,
          text: this.$t("common.loading"),
          spinner: "el-icon-loading",
          background: "rgba(0, 0, 0, 0.7)",
        });

        await this.createAssignments(this.selectedAssignment.id);
        this.resetAssignmentLibrary();
        this.primaryLoading.close();
        this.primaryLoading = false;
        this.$emit("done");
      } catch (error) {
        this.errors = error;
        this.primaryLoading.close();
        this.primaryLoading = false;
      }
    },
    reset() {
      this.resetAssignments();
    },

    handleAssignToAnotherClass() {
      const assignments = this.assignments;
      assignments.push({
        name: this.name,
      });
      this.updateBuildingAssignment({
        assignments: assignments,
      });
    },
    emptyCheck() {
      this.$refs.AssignToClassDetail.every(x => {
        if (x.assignment.name || x.assignment.groupIds || x.assignment.startDate || x.assignment.dueDate)
          this.emptyCheckStatus = 1;
      });
      return this.emptyCheckStatus;
    },
    showAssignmentName(name) {
      this.name = name;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../scss/_colors.scss";

#leave-modal-title {
  font-weight: 700;
  font-size: 22px;
  line-height: 32px;
  text-align: center;
  color: $black;
  margin: 0 auto;
}

#leave-modal-desc {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: $black;
  margin: 4px auto;
}

.elsa-button.confirm-delete,
.elsa-button.confirm-delete:hover {
  border-radius: 8px;
  min-width: 120px;
  height: 48px;
}

.elsa-button.confirm-cancel,
.elsa-button.confirm-cancel:hover {
  border-radius: 8px;
  min-width: 120px;
  height: 48px;
}

ul {
  list-style-type: none;
  padding-left: 0px;
  margin-bottom: 0px;
  margin-top: 0px;
}

.section-container {
  box-sizing: border-box;
  border-radius: 8px;
}

.assignment-name-from {
  padding-bottom: 0;
}

.assignment-name {
  margin-bottom: 0 !important;
}

.assigned-classes-list {
  padding: 24px;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), #efeff4;
  border-radius: 8px;
  margin-bottom: 24px;
  li {
    box-shadow: inset 0px -1px 0px #e6e6e6;
  }

  > :last-child {
    margin-bottom: 0px;
    box-shadow: none;
  }
}

.section-header {
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.25px;
  color: #8e8e93;
  margin: 0;
  margin-bottom: 1rem;
}

::v-deep {
  .el-loading-mask {
    border-radius: 8px !important;
  }
  .el-form .el-form-item {
    margin-bottom: 24px;
  }

  .el-form .el-form-item .el-form-item__label {
    margin-bottom: 8px;
    color: $black;
    font-weight: 600;
    line-height: 24px;
  }

  .el-button--text {
    color: $hyperlink;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }

  .el-dialog.elsa-modal {
    border-radius: 8px;
    .el-dialog__header,
    .el-dialog__body {
      //padding: 40px 40px 0 40px;
      // display: flex;
      background-color: white;
    }

    &.signup .el-dialog__body {
      padding: 0;
    }

    .el-dialog__header {
      border-top-left-radius: 16px;
      border-top-right-radius: 16px;
      min-height: 27.5px;
      padding: 32px 32px 24px;
    }

    .el-dialog__body {
      border-top: none;
      & > *:not(p):not(.info) {
        padding-bottom: 6px;
        display: flex;
        flex-direction: column;
      }
    }

    .el-dialog__footer {
      padding: 0 32px 48px 32px;
      justify-content: space-between;
    }
  }
}

h1 {
  font-weight: 700;
  font-size: 24px;
  line-height: 36px;
  letter-spacing: 0em;
}

::v-deep .el-dialog.elsa-modal .el-dialog__body {
  opacity: 1;
  padding: 0 32px;
}

::v-deep .el-input__prefix,
.el-input__suffix {
  top: 4px;
}

::v-deep .assignment-name .el-form-item__content .el-input .el-input__inner {
  background: #e6e6e6;
  border: 1px solid #d1d1d6;
  color: $black;
  opacity: 1;
  letter-spacing: 0.25px;
}
</style>
